import { useShouldShowPostPurchaseBookingLinks } from '@shared/hooks/useShouldShowPostPurchaseBookingLinks';
import { useEventAccommodationsCheck } from '@shared/hooks/useEventAccommodationsCheck';
import { useRouterHelper } from '@shared/core';
import { withWindow } from '@shared/utils/withWindow';

// used to avoid unexpected URL escaping
const PC_ID_PLACEHOLDER = 'PC_ID_PLACEHOLDER';

// Upon payment completion, the user will be redirected back to the 'successUrlTemplate'
//   or 'failureUrlTemplate' depending on the payment outcome.  The template is expected to contain
//   a '${placeholder}' for `purchaseContextId` using that exact format
const PC_ID_TEMPLATE_CONTENT = '${purchaseContextId}'; // yes, that literal string, no substitution

/**
 * returnPath should NOT have any parameters of its own.
 */
export const composePurchaseReturnUrlTemplate = (returnPath: string): string => {
  const delimiter = returnPath.includes('?') ? '&' : '?';
  const template = `${returnPath}${delimiter}pcId=${PC_ID_TEMPLATE_CONTENT}`;
  return template;
};

export const usePurchaseConfirmationLink = (eventHandle: string | undefined | null, isAffiliate: boolean = false) => {
  const { isGuestRoute } = useRouterHelper();

  // A guest who RSVP'd not attending is sent to their order summary, not to a hotel page.
  // Suppressing the CTA on the accommodations page would still strand them there — the
  // redirect itself is the upsell, so the decision belongs here, before it happens.
  const { shouldShowBookingLinks } = useShouldShowPostPurchaseBookingLinks({ eventHandle, skip: !isGuestRoute || isAffiliate });
  const { accommodationsPagePath, travelPagePath, hasTravelAccommodationListings } = useEventAccommodationsCheck({
    eventHandle: eventHandle || '',
  });

  return (initialSuccessUrl: string): string => {
    if (!isGuestRoute || isAffiliate || !shouldShowBookingLinks) {
      return initialSuccessUrl;
    }

    const baseUrl = withWindow(g => g.location.origin, '');

    // Use URLSearchParams for proper escaping, then manually replace the pcId value
    const params = new URLSearchParams({
      pcId: PC_ID_PLACEHOLDER,
      utm_medium: 'web',
      utm_source: 'confirmation_registry_purchase',
      utm_campaign: 'next_steps',
    });

    // Replace the placeholder with the unescaped template literal
    const queryString = params.toString().replace(PC_ID_PLACEHOLDER, PC_ID_TEMPLATE_CONTENT);

    const path =
      accommodationsPagePath ?? (travelPagePath && hasTravelAccommodationListings ? travelPagePath : `/${encodeURIComponent(eventHandle || '')}/registry/accommodations`);

    const promotionalReturnUrlTemplate = `${baseUrl}${path}?${queryString}`;
    return promotionalReturnUrlTemplate;
  };
};
